/**
 * WeaponsController — loads and deserializes weapon JSON configs
 * into typed Weapon class instances.
 *
 * Auto-discovers weapons/weapon-pieces/*.json via require.context.
 */
import type { ClausePredicate } from './weaponStatusesStore';
import { VerbType } from '../../dsl/semantics';
import { StatType } from '../enums/stats';
import { resolveEffectStat } from '../enums/stats';
import { checkKeys, checkIdAndName, VALID_VALUE_NODE_KEYS, VALID_CLAUSE_KEYS, VALID_EFFECT_KEYS, VALID_EFFECT_WITH_KEYS, validateEffect as validateEffectSemantics, validateNonNegativeValues } from './validationUtils';
import { LocaleKey, resolveEventName } from '../../locales/gameDataLocale';

// ── Validation ──────────────────────────────────────────────────────────────
const VALID_PROPERTIES_KEYS = new Set(['id', 'type', 'rarity']);
const VALID_METADATA_KEYS = new Set(['originId', 'dataSources', 'icon', 'nameId', 'dataStatus']);
const VALID_TOP_KEYS = new Set(['skills', 'properties', 'metadata', 'segments']);

function validateValueNode(wv: Record<string, unknown>, path: string): string[] {
  const errors = checkKeys(wv, VALID_VALUE_NODE_KEYS, path);
  if ('verb' in wv && typeof wv.verb !== 'string') errors.push(`${path}.verb: must be a string`);
  if ('operation' in wv && typeof wv.operation !== 'string') errors.push(`${path}.operation: must be a string`);
  return errors;
}

function validateLocalEffect(ef: Record<string, unknown>, path: string): string[] {
  const errors = checkKeys(ef, VALID_EFFECT_KEYS, path);
  if (typeof ef.verb !== 'string') errors.push(`${path}.verb: must be a string`);
  if (typeof ef.object !== 'string') errors.push(`${path}.object: must be a string`);
  errors.push(...validateEffectSemantics(ef, path));
  if (ef.with) {
    const w = ef.with as Record<string, unknown>;
    errors.push(...checkKeys(w, VALID_EFFECT_WITH_KEYS, `${path}.with`));
    if (w.value) errors.push(...validateValueNode(w.value as Record<string, unknown>, `${path}.with.value`));
  }
  return errors;
}

/** Validate a raw weapon JSON entry. Returns an array of error messages (empty = valid). */
export function validateWeapon(json: Record<string, unknown>): string[] {
  const errors = checkKeys(json, VALID_TOP_KEYS, 'root');
  errors.push(...validateNonNegativeValues(json, 'root'));

  if (!Array.isArray(json.skills)) errors.push('root.skills: must be an array');
  else if (json.skills.some(s => typeof s !== 'string')) errors.push('root.skills: all entries must be strings');

  // Root-level `clause` is rejected — clauses MUST live inside segments.
  if ('clause' in json) errors.push('root.clause: not allowed — move clause effects into segments[0].clause');

  if (json.segments) {
    if (!Array.isArray(json.segments)) errors.push('root.segments: must be an array');
    else (json.segments as Record<string, unknown>[]).forEach((s, si) => {
      const segClause = (s as { clause?: unknown[] }).clause;
      if (Array.isArray(segClause)) {
        (segClause as Record<string, unknown>[]).forEach((c, i) => {
          const clauseErrors = checkKeys(c, VALID_CLAUSE_KEYS, `segments[${si}].clause[${i}]`);
          errors.push(...clauseErrors);
          if (Array.isArray(c.effects)) {
            (c.effects as Record<string, unknown>[]).forEach((ef, j) => errors.push(...validateLocalEffect(ef, `segments[${si}].clause[${i}].effects[${j}]`)));
          }
        });
      }
    });
  }

  const props = json.properties as Record<string, unknown> | undefined;
  if (!props) { errors.push('root.properties: required'); return errors; }
  errors.push(...checkKeys(props, VALID_PROPERTIES_KEYS, 'properties'));
  errors.push(...checkIdAndName(props, 'properties'));
  if (typeof props.type !== 'string') errors.push('properties.type: must be a string');
  if (typeof props.rarity !== 'number') errors.push('properties.rarity: must be a number');

  const meta = json.metadata as Record<string, unknown> | undefined;
  if (meta) {
    errors.push(...checkKeys(meta, VALID_METADATA_KEYS, 'metadata'));
  }

  return errors;
}

// ── Weapon class ────────────────────────────────────────────────────────────

/** A weapon definition. Maps 1:1 to the JSON shape. */
export class Weapon {
  readonly skills: string[];
  readonly segments: { clause?: ClausePredicate[] }[];
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly rarity: number;
  readonly dataSources: string[];
  readonly originId: string;
  /** Resolved icon URL (set by loader after construction). */
  icon?: string;

  constructor(json: Record<string, unknown>) {
    const props = (json.properties ?? {}) as Record<string, unknown>;
    const meta = (json.metadata ?? {}) as Record<string, unknown>;

    this.skills = (json.skills ?? []) as string[];
    this.segments = (json.segments ?? []) as { clause?: ClausePredicate[] }[];
    this.id = (props.id ?? '') as string;
    this.name = this.id ? resolveEventName(LocaleKey.weapon(this.id)) : '';
    this.type = (props.type ?? '') as string;
    this.rarity = (props.rarity ?? 0) as number;
    this.dataSources = (meta.dataSources ?? []) as string[];
    this.originId = (meta.originId ?? '') as string;
  }

  /** All clause predicates flattened across segments. */
  get clause(): ClausePredicate[] {
    return this.segments.flatMap(s => s.clause ?? []);
  }

  /** Get base attack values (from clause APPLY STAT objectId=BASE_ATTACK). */
  get baseAttackValues(): number[] {
    for (const clause of this.clause) {
      for (const ef of clause.effects) {
        if (ef.verb === VerbType.APPLY && resolveEffectStat(ef) === StatType.BASE_ATTACK) {
          const wv = (ef.with as Record<string, unknown>)?.value as { value?: number | number[] } | undefined;
          const v = wv?.value;
          return v == null ? [] : Array.isArray(v) ? v : [v];
        }
      }
    }
    return [];
  }

  /** Get base attack at a specific weapon level (1-indexed). */
  getBaseAttack(level: number): number {
    return this.baseAttackValues[level - 1] ?? 0;
  }

  /** Get the generic skill IDs (slots 1 and 2). */
  get genericSkillIds(): string[] {
    return this.skills.slice(0, 2);
  }

  /** Get the named skill ID (slot 3), if present. */
  get namedSkillId(): string | undefined {
    return this.skills[2];
  }

  /** Serialize back to the JSON shape. */
  serialize(): Record<string, unknown> {
    return {
      skills: this.skills,
      properties: {
        id: this.id,
        type: this.type,
        rarity: this.rarity,
      },
      metadata: {
        ...(this.dataSources.length > 0 ? { dataSources: this.dataSources } : {}),
        originId: this.originId,
      },
      ...(this.segments.length > 0 ? { segments: this.segments } : {}),
    };
  }

  /** Deserialize from JSON with validation. */
  static deserialize(json: Record<string, unknown>, source?: string): Weapon {
    const errors = validateWeapon(json);
    if (errors.length > 0) {
      const id = (json.properties as Record<string, unknown>)?.id ?? 'unknown';
      console.warn(`[Weapon] Validation errors in ${source ?? id}:\n  ${errors.join('\n  ')}`);
    }
    return new Weapon(json);
  }
}

// ── Icon auto-discovery ─────────────────────────────────────────────────────

const weaponIconContext = require.context('../../assets/weapons', false, /\.(png|webp)$/);
const WEAPON_ICONS: Record<string, string> = {};
for (const key of weaponIconContext.keys()) {
  const match = key.match(/\.\/(.+)\.(png|webp)$/);
  if (match) {
    const assetName = match[1].replace(/_icon$/, '');
    WEAPON_ICONS[assetName] = weaponIconContext(key);
  }
}

/** Hard-wired ID → asset: `<id.toLowerCase()>_icon.(png|webp)`. */
function resolveWeaponIcon(weaponId: string): string | undefined {
  return WEAPON_ICONS[weaponId.toLowerCase()];
}

// ── Loader ──────────────────────────────────────────────────────────────────

/** All weapons indexed by ID (e.g. "FORGEBORN_SCATHE"). */
const weaponCache = new Map<string, Weapon>();
/** Custom weapon overlay (takes priority over built-in). */
const customWeaponCache = new Map<string, Weapon>();

const weaponContext = require.context('./weapons', true, /\/[^/]+\/[^/]+\.json$/);
for (const key of weaponContext.keys()) {
  // Match only base weapon files (e.g. ./forgeborn-scathe/forgeborn-scathe.json)
  // Skip skills/, statuses/ subdirs and generic/
  if (key.includes('/skills/') || key.includes('/statuses/') || key.includes('/generic/')) continue;
  // Skip effects files
  if (key.includes('-effects.json')) continue;

  const json = weaponContext(key) as Record<string, unknown>;
  const weapon = Weapon.deserialize(json, key);
  if (weapon.id) {
    weapon.icon = resolveWeaponIcon(weapon.id);
    weaponCache.set(weapon.id, weapon);
  }
}

/** Get a weapon by ID (e.g. "FORGEBORN_SCATHE"). Checks custom first, then built-in. */
export function getWeapon(weaponId: string): Weapon | undefined {
  return customWeaponCache.get(weaponId) ?? weaponCache.get(weaponId);
}

/** Get all weapon IDs (custom + built-in, custom overrides built-in). */
export function getAllWeaponIds(): string[] {
  const ids = new Set(customWeaponCache.keys());
  weaponCache.forEach((_, id) => ids.add(id));
  return Array.from(ids);
}

/** Get all weapons (custom + built-in). */
export function getAllWeapons(): readonly Weapon[] {
  const merged = new Map<string, Weapon>();
  weaponCache.forEach((w, id) => merged.set(id, w));
  customWeaponCache.forEach((w, id) => merged.set(id, w));
  const result: Weapon[] = [];
  merged.forEach(w => result.push(w));
  return result;
}

/** Get all weapons filtered by weapon type. */
export function getWeaponsByType(weaponType: string): readonly Weapon[] {
  return getAllWeapons().filter(w => w.type === weaponType);
}

/**
 * Find the weapon containing the given skillId in its `skills[]`, plus the
 * 0-based position (→ skill1/2/3). Returns undefined when no weapon owns the
 * skill. Used by the value resolver to look up `RANK of THIS WEAPON`.
 */
export function findWeaponBySkillId(skillId: string): { weapon: Weapon; index: number } | undefined {
  for (const w of getAllWeapons()) {
    const idx = w.skills.indexOf(skillId);
    if (idx >= 0) return { weapon: w, index: idx };
  }
  return undefined;
}

// ── Custom registration ─────────────────────────────────────────────────────

/** Register a custom weapon (overlay — takes priority over built-in). */
export function registerCustomWeapon(json: Record<string, unknown>, icon?: string): Weapon {
  const weapon = Weapon.deserialize(json, 'custom');
  weapon.icon = icon ?? resolveWeaponIcon(weapon.id);
  customWeaponCache.set(weapon.id, weapon);
  return weapon;
}

/** Deregister a custom weapon by ID. */
export function deregisterCustomWeapon(weaponId: string): void {
  customWeaponCache.delete(weaponId);
}
